import { MeterBar, RingMeter, StatStrip, StatTile, formatInt, plural } from '@orgus/ui';
import { StatStripSkeleton } from '../fleet/StatStrip.tsx';
import type { Verdict } from '../glance/PageVerdict.tsx';
import type { FleetSummary } from './model.ts';

function trendText(delta: number | null): string {
  if (delta === null) return 'No trend yet';
  if (delta === 0) return 'Unchanged over time';
  return `${delta > 0 ? 'Up' : 'Down'} ${formatInt(Math.abs(delta))} over time`;
}

/** Discipline's one answer: are the repos keeping their house rules, and which need a person. */
export function disciplineVerdict(s: FleetSummary): Verdict {
  if (!s.checked) return { tone: 'neutral', title: 'No repo checked yet', line: 'Scores show once each machine reports.' };
  if (s.errors) return { tone: 'danger', title: `${plural(s.errors, 'rule error')} to fix`, line: `${plural(s.needAttention, 'repo')} below the bar.` };
  if (s.needAttention) return { tone: 'attention', title: `${plural(s.needAttention, 'repo')} need attention`, line: `${plural(s.warns, 'warning')} open.` };
  return { tone: 'ok', title: 'Every repo keeps its house rules', line: `Fleet score ${formatInt(s.score ?? 0)} of 100.` };
}

/** Fleet score, repos that need attention, open errors, debt left and waived violations, as shapes. */
export function SummaryStrip({
  summary,
  ratchet,
  onWaivers,
  onViolations,
}: {
  summary: FleetSummary;
  ratchet?: { repos: number; remaining: number; total: number; fresh: number } | null;
  onWaivers?: () => void;
  onViolations?: () => void;
}) {
  const open = summary.errors + summary.warns;
  return (
    <StatStrip label="Discipline across every repo" className={ratchet ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}>
      <StatTile
        label="Fleet score"
        featured
        value={summary.score ?? 0}
        of={100}
        sub={trendText(summary.trend)}
        trend={<RingMeter value={(summary.score ?? 0) / 100} size={40} tone={3} label={`Fleet score ${formatInt(summary.score ?? 0)} of 100`} />}
      />
      <StatTile
        label="Need attention"
        value={summary.needAttention}
        of={summary.total}
        attention={summary.needAttention > 0}
        quietAtZero
        sub={summary.needAttention ? 'Below 90 or failed' : `${formatInt(summary.checked)} of ${formatInt(summary.total)} checked`}
      />
      <StatTile label="Open violations" value={open} quietAtZero sub={summary.errors ? `${plural(summary.errors, 'error')}, ${plural(summary.warns, 'warning')}` : plural(summary.warns, 'warning')} onOpen={onViolations} />
      {ratchet && (
        <StatTile
          label="Debt left"
          value={ratchet.remaining}
          of={ratchet.total}
          sub={ratchet.fresh ? `${formatInt(ratchet.fresh)} new on top` : `${plural(ratchet.repos, 'repo')} on a ratchet`}
          visual={<MeterBar value={ratchet.total ? 1 - ratchet.remaining / ratchet.total : 1} tone={ratchet.fresh ? 'attention' : 3} label={`${formatInt(ratchet.total - ratchet.remaining)} of ${formatInt(ratchet.total)} debt paid down`} />}
        />
      )}
      <StatTile label="Waived" value={summary.waived} quietAtZero unit={summary.waived === 1 ? 'violation' : 'violations'} sub="Under a waiver" onOpen={onWaivers} />
    </StatStrip>
  );
}

export { StatStripSkeleton as SummaryStripSkeleton };
